import type { InvoiceModel } from '@/Models/InvoiceModel';
import Grid from '@mui/material/Grid';
import React, { useState } from 'react';
import type OrderNomenclature from '../../Models/OrderNomenclature';
import type Procedure from '../../Models/Procedure';
import AmountsGrid from './AmountsGrid';
import InvoiceRemarksGrid from './InvoiceRemarksGrid';
import NomenclatureTableGrid from './NomenclatureTableGrid';
import Styles from './OrderNomenclatureGrid.module.scss';
import SearchNomenclatureGrid from './SearchNomenclatureGrid';

function OrderNomenclatureGrid({ invoiceModel, procedures, getInvoiceInformation, updateInvoiceModel }: Readonly<{
    invoiceModel: InvoiceModel; procedures: Procedure[]; getInvoiceInformation: Function; updateInvoiceModel: Function;
}>): React.ReactElement {
    const [nomenclatures, setNomenclatures] = useState<OrderNomenclature[]>(invoiceModel.ProcedureAndProductNomenclatures);
    const [newPatientAmount, setNewPatientAmount] = useState<string>(invoiceModel.AmountPatient.toFixed(2));
    const [newTotalAmount, setNewTotalAmount] = useState<string>(invoiceModel.TotalAmount.toFixed(2));
    const [amountPatientEdited, setAmountPatientEdited] = useState(false);
    const [amountTotalEdited, setAmountTotalEdited] = useState(false);
    const [totalCustomHonorarium, setTotalCustomHonorarium] = useState((invoiceModel.CustomHonorarium + invoiceModel.CustomSupplementHonorarium).toFixed(2));

    const changeTotalCustomHonorarium = (totalCustomHonorarium: string): void => {
        invoiceModel.CustomHonorarium = Number(totalCustomHonorarium);
        updateInvoiceModel();
        setTotalCustomHonorarium(totalCustomHonorarium);
    };

    const changeNomenclatures = (editedNomenclatures: OrderNomenclature[]): void => {
        setNomenclatures(editedNomenclatures);
        getInvoiceInformationWithManuallyAddedNomenclatures(editedNomenclatures);
    };

    const changeAmountPatient = (value: string): void => {
        setNewPatientAmount(value);
        setAmountPatientEdited(true);
    };

    const changeTotalAmount = (value: string): void => {
        setNewTotalAmount(value);
        setAmountTotalEdited(true);
    };

    const checkNumericOnly = (event: React.KeyboardEvent<HTMLDivElement>, valueToCheck: string): void => {
        const key = event.key;
        if (key === '.' && valueToCheck.includes('.')) {
            event.preventDefault();
        }

        const availableKeys = [
            'Backspace',
            'Tab',
            'Delete',
            '.',
            'Home', 'End',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
            'Enter',
        ];
        if (!availableKeys.includes(key)) {
            event.preventDefault();
        }

        if (key === 'Enter') {
            getInvoiceInformationWithManuallyAddedNomenclatures();
        }
    };

    const getInvoiceInformationWithManuallyAddedNomenclatures = (editedNomenclatures?: OrderNomenclature[]): void => {
        const manuallyAddedNomens = (editedNomenclatures ?? nomenclatures).filter((item) => item.IsManuallyAdded);
        getInvoiceInformation(false, false, amountPatientEdited, amountTotalEdited, manuallyAddedNomens);
    };

    return (
        <Grid container spacing={4} className={Styles.gridContainer}>
            <AmountsGrid invoiceModel={invoiceModel} newPatientAmount={newPatientAmount} newTotalAmount={newTotalAmount} totalCustomHonorarium={totalCustomHonorarium} changeAmountPatient={changeAmountPatient} changeTotalAmount={changeTotalAmount} checkNumericOnly={checkNumericOnly} getInvoiceInformation={getInvoiceInformationWithManuallyAddedNomenclatures} />
            <SearchNomenclatureGrid invoiceModel={invoiceModel} procedures={procedures} nomenclatures={nomenclatures} changeNomenclatures={changeNomenclatures} />
            <InvoiceRemarksGrid invoiceModel={invoiceModel} />
            <NomenclatureTableGrid invoiceModel={invoiceModel} nomenclatures={nomenclatures} totalCustomHonorarium={totalCustomHonorarium} changeNomenclatures={changeNomenclatures} checkNumericOnly={checkNumericOnly} updateInvoiceModel={updateInvoiceModel} setTotalCustomHonorarium={changeTotalCustomHonorarium} getInvoiceInformation={getInvoiceInformationWithManuallyAddedNomenclatures} />
        </Grid>
    );
}
export default OrderNomenclatureGrid;
